import type { TFunction } from 'i18next';
import type { OpenItemDto } from '@/store/useOpenItemsStore';
import { formatDate, parseIsoDate } from '@/utils/dateUtils';
import { numberLocale } from '@/utils/locale';
import { formatMoney } from '@/utils/money';

export const MANUAL_SOURCE = 'manual';
/** A report ("Meldung") from an employee to Verwaltung — stored like a manual item, no assignee. */
export const REPORT_SOURCE = 'report';

const SOURCE_ROUTES: Record<string, (item: OpenItemDto) => string | null> = {
  'timeClock.unconfirmed': () => '/timeclock',
  'timeClock.unconfirmedByEmployee': () => '/timeclock/admin',
  'absences.pendingApproval': () => '/absences/admin',
  'invoices.overdue': (item) => (item.referenceId ? `/invoices/${item.referenceId}` : '/invoices'),
};

export function isReport(item: OpenItemDto): boolean {
  return item.source === REPORT_SOURCE;
}

/** Derived by the server at read time — neither a manual item nor a report. */
export function isSystemItem(item: OpenItemDto): boolean {
  return item.source !== MANUAL_SOURCE && !isReport(item);
}

/** "2026-09" → "September 2026" in the UI language. */
function formatMonth(month: string, lang: string): string {
  const date = parseIsoDate(`${month}-01`);
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleDateString(numberLocale(lang), { month: 'long', year: 'numeric' });
}

/** Invariant "1234.50" → "1.234,50 €". */
function formatAmount(amount: string, lang: string): string {
  const value = Number(amount);
  if (Number.isNaN(value)) return amount;
  return formatMoney(value, { lang });
}

function formatDay(day: string | undefined): string {
  return day ? formatDate(parseIsoDate(day)) : '';
}

function formatParams(params: Record<string, string>, lang: string): Record<string, unknown> {
  const formatted: Record<string, unknown> = { ...params };
  if (params.count !== undefined) formatted.count = Number(params.count);
  if (params.month) formatted.month = formatMonth(params.month, lang);
  if (params.from) formatted.from = formatDay(params.from);
  if (params.to) formatted.to = formatDay(params.to);
  if (params.amount) formatted.amount = formatAmount(params.amount, lang);
  return formatted;
}

/**
 * Display text of an open item: the title of a manual item or report, the localized source
 * text with formatted params of a system item.
 */
export function getOpenItemText(item: OpenItemDto, t: TFunction, lang: string): string {
  if (!isSystemItem(item)) return item.title ?? '';
  const params = item.params ?? {};
  return t(getSourceKey(item.source ?? '', params), formatParams(params, lang));
}

function getSourceKey(source: string, params: Record<string, string>): string {
  if (source === 'invoices.overdue' && params.customerName) {
    return 'openItems:sources.invoices.overdueWithCustomer';
  }
  if (source === 'absences.pendingApproval' && params.from === params.to) {
    return 'openItems:sources.absences.pendingApprovalOneDay';
  }
  return `openItems:sources.${source}`;
}

/** Where a system item is resolved; null for manual items and reports. */
export function getOpenItemLink(item: OpenItemDto): string | null {
  if (!isSystemItem(item)) return null;
  return SOURCE_ROUTES[item.source ?? '']?.(item) ?? null;
}

/** Due date strictly before today (local calendar day). */
export function isOverdue(dueDate: string | null | undefined, todayIso: string): boolean {
  return !!dueDate && dueDate < todayIso;
}

export function formatDueDate(dueDate: string | null | undefined): string {
  return dueDate ? formatDate(parseIsoDate(dueDate)) : '–';
}
